import { useEffect } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { Connection } from '@domain/connection/models/Connection';
import { ClientType } from '@domain/connection/models/Connector';
import { APP_CONFIG } from '@config/app.config';
import { useConnections } from '@features/connections/generic/hooks/useConnections';

interface WarehouseConnectionSelectProps {
    connectorType: ClientType;
    selectedConnection: Connection | null;
    onSelect: (connection: Connection) => void;
    onCreateConnection: () => void;
    connectorName: string;
}

const WarehouseConnectionSelect = ({
    connectorType,
    selectedConnection,
    onSelect,
    onCreateConnection,
    connectorName,
}: WarehouseConnectionSelectProps) => {
    const { connections: allConnections } = useConnections();
    const connections = (allConnections ?? []).filter(
        connection => connection.connector.type === connectorType,
    );
    const isLoading = allConnections === undefined;

    useEffect(() => {
        if (connections.length === 1 && selectedConnection?.id !== connections[0].id) {
            onSelect(connections[0]);
        }
    }, [connections, selectedConnection, onSelect]);

    return (
        <Stack gap={2}>
            <Typography variant="body2" color="text.secondary">
                Select a connection to sync with
            </Typography>

            {isLoading ? (
                <Box display="flex" justifyContent="center" py={1}>
                    <CircularProgress size={20} />
                </Box>
            ) : (
                <Stack gap={2} direction="row" flexWrap="wrap">
                    {connections.map(connection => (
                        <Button
                            key={connection.id}
                            variant="outlined"
                            onClick={() => onSelect(connection)}
                            sx={{
                                borderColor: selectedConnection?.id === connection.id ? 'primary.main' : 'divider',
                                color: selectedConnection?.id === connection.id ? 'primary.main' : 'text.primary',
                                borderWidth: 1,
                                borderRadius: 1,
                                padding: 2,
                            }}
                        >
                            <Typography variant="subtitle2">
                                {connection.name}
                            </Typography>
                        </Button>
                    ))}

                    {connections.length === 0 && (
                        <>
                            <Button
                                variant="outlined"
                                onClick={onCreateConnection}
                                sx={{
                                    borderColor: 'divider',
                                    borderWidth: 1,
                                    borderRadius: 1,
                                    padding: 2,
                                    borderStyle: 'dashed',
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Add sx={{ fontSize: 16 }} />
                                    <Typography variant="subtitle2" color="text.primary">
                                        Create new connection
                                    </Typography>
                                </Stack>
                            </Button>
                        </>
                    )}
                </Stack>
            )}
        </Stack>
    );
};

export default WarehouseConnectionSelect;
